import React, { useState } from 'react';
import { Copy, Check, Plus, Trash2, Key, AlertTriangle, Loader2, User, X, CreditCard, ExternalLink, Sparkles, Shield, Bot } from 'lucide-react';
import { apiClient } from '../lib/api';
import { useUser, useOrganizationList } from '@clerk/clerk-react';
import { useApiKeys, useCreateApiKey, useDeleteApiKey, useProvisioningTokens, useCreateProvisioningToken, useRevokeProvisioningToken, useSubKeys, useRevokeSubKey, useProviderKeys, useSupportedProviders, useSetProviderKey, useDeleteProviderKey } from '../hooks/useSettings';
import SidebarLayout from '../components/SidebarLayout';
import { Card, CardHeader, CardBody } from '../components/ui/card';
import { Button } from '../components/ui/button';

function ApiKeyRow({ apiKey, onDelete, deleting }) {
  const [copied, setCopied] = useState(false);

  // Full key is only available on newly created keys, otherwise we only have key_prefix
  const hasFullKey = !!apiKey.key;
  const displayKey = hasFullKey
    ? apiKey.key
    : `${apiKey.key_prefix}${'•'.repeat(52)}`;

  const copyKey = () => {
    if (hasFullKey) {
      navigator.clipboard.writeText(apiKey.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="flex items-center justify-between p-4 bg-[#141416] rounded-xl border border-white/[0.04] group hover:border-white/[0.08] transition-colors">
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
          <Key className="w-5 h-5 text-amber-500" />
        </div>
        <div>
          <div className="font-medium text-white mb-1">{apiKey.name || 'API Key'}</div>
          <code className="text-xs text-zinc-500 font-mono block mb-1">
            {displayKey}
          </code>
          <span className="text-xs text-zinc-600">Created {formatDate(apiKey.created_at)}</span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {hasFullKey && (
          <button
            onClick={copyKey}
            className="p-2 text-zinc-600 hover:text-zinc-400 hover:bg-white/[0.04] rounded-lg transition-colors"
            title="Copy"
          >
            {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
          </button>
        )}

        <button
          onClick={() => onDelete(apiKey.id)}
          disabled={deleting}
          className="p-2 text-zinc-600 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
          title="Delete"
        >
          {deleting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Trash2 className="w-4 h-4" />
          )}
        </button>
      </div>
    </div>
  );
}

function NewKeyDisplay({ apiKey, onDismiss }) {
  const [copied, setCopied] = useState(false);
  const [copiedEnv, setCopiedEnv] = useState(false);

  const copyKey = () => {
    navigator.clipboard.writeText(apiKey.key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyEnvVar = () => {
    navigator.clipboard.writeText(`KALIBR_API_KEY=${apiKey.key}`);
    setCopiedEnv(true);
    setTimeout(() => setCopiedEnv(false), 2000);
  };

  return (
    <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Check className="w-5 h-5 text-green-500" />
          <span className="text-green-500 font-medium">API Key Created: {apiKey.name}</span>
        </div>
        <button onClick={onDismiss} className="text-zinc-500 hover:text-zinc-300">
          <X className="w-4 h-4" />
        </button>
      </div>
      <p className="text-sm text-zinc-400 mb-3">
        Copy this key now. You won't be able to see it again!
      </p>
      <div className="flex items-center gap-2">
        <code className="flex-1 bg-[#09090b] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white font-mono break-all">
          {apiKey.key}
        </code>
        <Button onClick={copyKey} variant="secondary" icon={copied ? Check : Copy}>
          {copied ? 'Copied!' : 'Copy'}
        </Button>
        <Button onClick={copyEnvVar} variant="ghost" icon={copiedEnv ? Check : Copy}>
          {copiedEnv ? 'Copied!' : 'Copy as .env'}
        </Button>
      </div>
    </div>
  );
}

export default function Settings() {
  const { user } = useUser();

  const { userMemberships } = useOrganizationList({ userMemberships: true });

  const { data: apiKeys, isLoading } = useApiKeys();
  const createKey = useCreateApiKey();
  const deleteKey = useDeleteApiKey();


  const { data: provTokens, isLoading: provTokensLoading } = useProvisioningTokens();
  const createProvToken = useCreateProvisioningToken();
  const revokeProvToken = useRevokeProvisioningToken();

  const { data: subKeys, isLoading: subKeysLoading } = useSubKeys();
  const revokeSubKey = useRevokeSubKey();

  // Provider keys vault
  const { data: providerKeys = [], isLoading: providerKeysLoading } = useProviderKeys();
  const { data: supportedProviders = [] } = useSupportedProviders();
  const setProviderKey = useSetProviderKey();
  const deleteProviderKey = useDeleteProviderKey();
  const [providerKeyInputs, setProviderKeyInputs] = useState({});
  const [providerKeyAdding, setProviderKeyAdding] = useState({});
  const [providerKeyError, setProviderKeyError] = useState({});
  const [providerKeyDeleting, setProviderKeyDeleting] = useState({});

  const [newKeyName, setNewKeyName] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [copiedTenant, setCopiedTenant] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
  const [createError, setCreateError] = useState(null);

  // Provisioning token state
  const [newTokenName, setNewTokenName] = useState('');
  const [newlyCreatedToken, setNewlyCreatedToken] = useState(null);
  const [provCreateError, setProvCreateError] = useState(null);
  const [revokingTokenId, setRevokingTokenId] = useState(null);
  const [confirmRevokeTokenId, setConfirmRevokeTokenId] = useState(null);

  // Agent keys state
  const [revokingSubKeyId, setRevokingSubKeyId] = useState(null);
  const [confirmRevokeSubKeyId, setConfirmRevokeSubKeyId] = useState(null);

  // Quick setup state
  const [copiedSetup, setCopiedSetup] = useState(false);

  // Get tenant ID - show org slug if user is in an org (for SDK compatibility),
  // otherwise fall back to user ID (for users without orgs)
  const firstOrg = userMemberships?.data?.[0]?.organization;
  const tenantId = firstOrg?.slug || firstOrg?.id || user?.id || '';

  const handleCreate = async () => {
    if (!newKeyName.trim()) return;
    setCreateError(null);
    try {
      const result = await createKey.mutateAsync({ name: newKeyName });
      // Store the newly created key so user can copy it
      setNewlyCreatedKey({
        key: result.api_key,
        name: newKeyName,
        ...result.key_info
      });
      setNewKeyName('');
    } catch (error) {
      console.error('Failed to create API key:', error);
      setCreateError(error?.response?.data?.detail || error?.message || 'Failed to create API key');
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await deleteKey.mutateAsync(id);
    } finally {
      setDeletingId(null);
    }
  };

  const handleUpgrade = async (targetTier) => {
    try {
      const result = await checkoutMutation.mutateAsync({
        targetTier,
        successUrl: `${window.location.origin}/register?purchased=true&plan=${targetTier}`,
        cancelUrl: `${window.location.origin}/settings?canceled=true`,
      });
      if (result.checkout_url) {
        window.location.href = result.checkout_url;
      }
    } catch (error) {
      console.error('Checkout failed:', error);
    }
  };

  const copyTenantId = () => {
    navigator.clipboard.writeText(tenantId);
    setCopiedTenant(true);
    setTimeout(() => setCopiedTenant(false), 2000);
  };

  const setupPrompt = `Read https://kalibr.systems/llms.txt for an overview of Kalibr, then read https://kalibr.systems/setup.txt for the complete setup reference. Integrate Kalibr outcome-aware routing into my project using the Router pattern. Set KALIBR_API_KEY and KALIBR_TENANT_ID from dashboard.kalibr.systems/settings.`;

  const copySetupPrompt = () => {
    navigator.clipboard.writeText(setupPrompt);
    setCopiedSetup(true);
    setTimeout(() => setCopiedSetup(false), 2000);
  };

  const handleCreateProvToken = async () => {
    if (!newTokenName.trim()) return;
    setProvCreateError(null);
    try {
      const result = await createProvToken.mutateAsync({ name: newTokenName });
      setNewlyCreatedToken({ ...result, token: result.provisioning_token });
      setNewTokenName('');
    } catch (error) {
      console.error('Failed to create provisioning token:', error);
      setProvCreateError(error?.response?.data?.detail || error?.message || 'Failed to create provisioning token');
    }
  };

  const handleRevokeProvToken = async (tokenId) => {
    setRevokingTokenId(tokenId);
    try {
      await revokeProvToken.mutateAsync(tokenId);
    } finally {
      setRevokingTokenId(null);
      setConfirmRevokeTokenId(null);
    }
  };

  const handleRevokeSubKey = async (keyId) => {
    setRevokingSubKeyId(keyId);
    try {
      await revokeSubKey.mutateAsync(keyId);
    } finally {
      setRevokingSubKeyId(null);
      setConfirmRevokeSubKeyId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatRelativeDate = (dateString) => {
    if (!dateString) return 'Never used';
    const diff = Date.now() - new Date(dateString).getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Used today';
    if (days === 1) return 'Last used 1 day ago';
    return `Last used ${days} days ago`;
  };

  return (
    <SidebarLayout>
      <main className="max-w-4xl mx-auto px-6 py-10">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-white tracking-tight mb-2">
            Settings
          </h1>
          <p className="text-zinc-500 text-sm">
            Manage your API keys and account settings
          </p>
        </div>

        {/* Tenant ID Section */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-zinc-500" />
              <div>
                <h2 className="font-semibold text-white">Tenant ID</h2>
                <p className="text-sm text-zinc-500 mt-1">
                  Use this ID to configure your SDK
                </p>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <div className="flex items-center gap-3">
              <code className="flex-1 bg-[#09090b] border border-white/[0.08] rounded-lg px-4 py-3 text-sm text-white font-mono">
                {tenantId || 'Loading...'}
              </code>
              <Button
                variant="secondary"
                onClick={copyTenantId}
                icon={copiedTenant ? Check : Copy}
                disabled={!tenantId}
              >
                {copiedTenant ? 'Copied!' : 'Copy'}
              </Button>
            </div>
          </CardBody>
        </Card>

        {/* API Keys Section */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-white">API Keys</h2>
                <p className="text-sm text-zinc-500 mt-1">
                  Use these keys to authenticate your SDK calls
                </p>
              </div>
            </div>
          </CardHeader>

          <CardBody className="space-y-4">
            {/* Create new key */}
            <div className="flex gap-3">
              <input
                type="text"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="Key name (e.g., Production)"
                className="flex-1 bg-[#09090b] border border-white/[0.08] rounded-lg px-4 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/30 focus:ring-1 focus:ring-white/30 transition-colors"
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              />
              <Button
                onClick={handleCreate}
                loading={createKey.isPending}
                icon={Plus}
              >
                Create Key
              </Button>
            </div>

            {/* Error message */}
            {createError && (
              <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1 text-sm">
                  <p className="text-red-500 font-medium">Failed to create API key</p>
                  <p className="text-zinc-400 mt-1">{createError}</p>
                </div>
                <button onClick={() => setCreateError(null)} className="text-zinc-500 hover:text-zinc-300">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Newly created key - show once for copying */}
            {newlyCreatedKey && (
              <NewKeyDisplay
                apiKey={newlyCreatedKey}
                onDismiss={() => setNewlyCreatedKey(null)}
              />
            )}

            {/* Warning */}
            <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="text-amber-500 font-medium mb-1">Keep your API keys secure</p>
                <p className="text-zinc-400">
                  Never share your API keys or commit them to version control. Use environment variables instead.
                </p>
              </div>
            </div>

            {/* Keys list */}
            {isLoading ? (
              <div className="space-y-3">
                {[...Array(2)].map((_, i) => (
                  <div key={i} className="h-24 bg-[#141416] rounded-xl animate-pulse" />
                ))}
              </div>
            ) : apiKeys?.length > 0 ? (
              <div className="space-y-3">
                {apiKeys.map((key) => (
                  <ApiKeyRow
                    key={key.id}
                    apiKey={key}
                    onDelete={handleDelete}
                    deleting={deletingId === key.id}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-zinc-500">
                No API keys yet. Create one to get started.
              </div>
            )}
          </CardBody>
        </Card>

        {/* Quick Setup - Install Prompt */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-amber-500" />
              <div>
                <h2 className="font-semibold text-white">Quick Setup</h2>
                <p className="text-sm text-zinc-500 mt-1">
                  Paste this into Claude Code or Cursor to integrate Kalibr into your project
                </p>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <div className="flex items-start gap-3">
              <code className="flex-1 bg-[#09090b] border border-white/[0.08] rounded-lg px-4 py-3 text-sm text-zinc-300 font-mono leading-relaxed block">
                {setupPrompt}
              </code>
              <Button
                onClick={copySetupPrompt}
                variant="primary"
                icon={copiedSetup ? Check : Sparkles}
              >
                {copiedSetup ? 'Copied!' : 'Hand to your coding agent'}
              </Button>
            </div>
          </CardBody>
        </Card>

        {/* Provisioning Tokens Section */}
        <Card className="mb-6">
          <CardHeader>
            <div>
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-zinc-500" />
                <div>
                  <h2 className="font-semibold text-white">Provisioning Tokens</h2>
                  <p className="text-sm text-zinc-500 mt-1">
                    Create a token once. Agents exchange it for API keys automatically — no dashboard visit required.
                  </p>
                </div>
              </div>
              <a
                href="https://kalibr.systems/docs"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors mt-2 inline-block ml-8"
              >
                Learn more about agent self-onboarding →
              </a>
            </div>
          </CardHeader>
          <CardBody className="space-y-4">
            {/* Create new token */}
            <div className="flex gap-3">
              <input
                type="text"
                value={newTokenName}
                onChange={(e) => setNewTokenName(e.target.value)}
                placeholder="Token name (e.g., CI Pipeline)"
                className="flex-1 bg-[#09090b] border border-white/[0.08] rounded-lg px-4 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/30 focus:ring-1 focus:ring-white/30 transition-colors"
                onKeyDown={(e) => e.key === 'Enter' && handleCreateProvToken()}
              />
              <Button
                onClick={handleCreateProvToken}
                loading={createProvToken.isPending}
                icon={Plus}
              >
                Create Token
              </Button>
            </div>

            {/* Error message */}
            {provCreateError && (
              <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1 text-sm">
                  <p className="text-red-500 font-medium">Failed to create provisioning token</p>
                  <p className="text-zinc-400 mt-1">{provCreateError}</p>
                </div>
                <button onClick={() => setProvCreateError(null)} className="text-zinc-500 hover:text-zinc-300">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Newly created token - show once */}
            {newlyCreatedToken && (
              <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-500" />
                    <span className="text-green-500 font-medium">Token Created: {newlyCreatedToken.name}</span>
                  </div>
                  <button onClick={() => setNewlyCreatedToken(null)} className="text-zinc-500 hover:text-zinc-300">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-sm text-zinc-400 mb-3">
                  Save this token. It won't be shown again.
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-[#09090b] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white font-mono break-all">
                    {newlyCreatedToken.token}
                  </code>
                  <Button
                    onClick={() => {
                      navigator.clipboard.writeText(newlyCreatedToken.token);
                    }}
                    variant="secondary"
                    icon={Copy}
                  >
                    Copy
                  </Button>
                  <Button
                    onClick={() => {
                      navigator.clipboard.writeText(`KALIBR_PROVISIONING_TOKEN=${newlyCreatedToken.token}`);
                    }}
                    variant="ghost"
                    icon={Copy}
                  >
                    Copy as env var
                  </Button>
                </div>
              </div>
            )}

            {/* Token list */}
            {provTokensLoading ? (
              <div className="space-y-3">
                {[...Array(2)].map((_, i) => (
                  <div key={i} className="h-20 bg-[#141416] rounded-xl animate-pulse" />
                ))}
              </div>
            ) : provTokens?.length > 0 ? (
              <div className="space-y-3">
                {provTokens.map((token) => (
                  <div
                    key={token.id}
                    className={`flex items-center justify-between p-4 bg-[#141416] rounded-xl border border-white/[0.04] group hover:border-white/[0.08] transition-colors ${token.revoked_at ? 'opacity-60' : ''}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                        <Shield className="w-5 h-5 text-purple-500" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`font-medium text-white ${token.revoked_at ? 'line-through' : ''}`}>
                            {token.name || 'Provisioning Token'}
                          </span>
                          {token.revoked_at && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded font-medium">
                              Revoked
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-zinc-500">
                          <span>Created {formatDate(token.created_at)}</span>
                          <span>·</span>
                          <span>
                            {token.max_uses
                              ? `${token.use_count || 0} / ${token.max_uses} uses`
                              : `${token.use_count || 0} uses`}
                          </span>
                          <span>·</span>
                          <span>{token.expires_at ? `Expires ${formatDate(token.expires_at)}` : 'Never expires'}</span>
                        </div>
                      </div>
                    </div>

                    {!token.revoked_at && (
                      <div className="flex items-center gap-2">
                        {confirmRevokeTokenId === token.id ? (
                          <>
                            <span className="text-xs text-red-400 mr-2">Revoke?</span>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleRevokeProvToken(token.id)}
                              loading={revokingTokenId === token.id}
                            >
                              Confirm
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setConfirmRevokeTokenId(null)}
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <button
                            onClick={() => setConfirmRevokeTokenId(token.id)}
                            className="p-2 text-zinc-600 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                            title="Revoke"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-zinc-500">
                No provisioning tokens yet. Create one to enable agent self-onboarding.
              </div>
            )}
          </CardBody>
        </Card>

        {/* Agent-Created Keys Section */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Bot className="w-5 h-5 text-zinc-500" />
              <div>
                <h2 className="font-semibold text-white">Agent-Created Keys</h2>
                <p className="text-sm text-zinc-500 mt-1">
                  API keys provisioned automatically by agents using your provisioning tokens.
                </p>
              </div>
            </div>
          </CardHeader>
          <CardBody className="space-y-4">
            {subKeysLoading ? (
              <div className="space-y-3">
                {[...Array(2)].map((_, i) => (
                  <div key={i} className="h-20 bg-[#141416] rounded-xl animate-pulse" />
                ))}
              </div>
            ) : subKeys?.length > 0 ? (
              <div className="space-y-3">
                {subKeys.map((subKey) => (
                  <div
                    key={subKey.id}
                    className="flex items-center justify-between p-4 bg-[#141416] rounded-xl border border-white/[0.04] group hover:border-white/[0.08] transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                        <Bot className="w-5 h-5 text-blue-500" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-white">{subKey.name || 'Agent Key'}</span>
                          {subKey.source && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-zinc-700 text-zinc-400 rounded font-medium">
                              {subKey.source}
                            </span>
                          )}
                        </div>
                        <code className="text-xs text-zinc-500 font-mono block mb-1">
                          {subKey.api_key_preview || `${subKey.key_prefix || 'sk_'}${'•'.repeat(20)}`}
                        </code>
                        <div className="flex items-center gap-3 text-xs text-zinc-500">
                          <span>Created {formatDate(subKey.created_at)}</span>
                          <span>·</span>
                          <span>{formatRelativeDate(subKey.last_used_at)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {confirmRevokeSubKeyId === subKey.id ? (
                        <>
                          <span className="text-xs text-red-400 mr-2">Revoke?</span>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleRevokeSubKey(subKey.id)}
                            loading={revokingSubKeyId === subKey.id}
                          >
                            Confirm
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setConfirmRevokeSubKeyId(null)}
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <button
                          onClick={() => setConfirmRevokeSubKeyId(subKey.id)}
                          className="p-2 text-zinc-600 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                          title="Revoke"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-zinc-500">
                No agent keys yet. Share a provisioning token with an agent to get started.
              </div>
            )}
          </CardBody>
        </Card>

        {/* Provider Keys Section */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/10 rounded-lg">
                <Shield className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h2 className="font-semibold text-white">Provider Keys</h2>
                <p className="text-sm text-zinc-500 mt-0.5">Used by <span className="font-mono text-xs bg-zinc-800 px-1.5 py-0.5 rounded">run_pipeline</span> to execute tasks server-side via the Kalibr MCP. Keys are encrypted at rest and never logged.</p>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            {providerKeysLoading ? (
              <div className="flex items-center gap-2 py-4 text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading...</div>
            ) : (
              <div className="space-y-3">
                {['openai', 'anthropic', 'deepseek', 'google'].map((provider) => {
                  const providerMeta = { openai: { label: 'OpenAI', hint: 'sk-...' }, anthropic: { label: 'Anthropic', hint: 'sk-ant-...' }, deepseek: { label: 'DeepSeek', hint: 'sk-...' }, google: { label: 'Google (Gemini)', hint: 'AI...' } }[provider];
                  const stored = providerKeys.find(k => k.provider === provider);
                  const isAdding = providerKeyAdding[provider];
                  const inputVal = providerKeyInputs[provider] || '';
                  const errMsg = providerKeyError[provider];
                  const isDeleting = providerKeyDeleting[provider];

                  return (
                    <div key={provider} className="flex items-center gap-3 py-3 border-b border-zinc-800 last:border-0">
                      <div className="w-32 flex-shrink-0">
                        <span className="text-sm font-medium text-white">{providerMeta.label}</span>
                      </div>
                      {stored ? (
                        <div className="flex items-center gap-2 flex-1">
                          <span className="font-mono text-sm text-zinc-400 bg-zinc-900 border border-zinc-700 rounded px-3 py-1.5 flex-1">{stored.masked}</span>
                          <button
                            onClick={async () => {
                              setProviderKeyAdding(p => ({ ...p, [provider]: true }));
                              setProviderKeyError(p => ({ ...p, [provider]: null }));
                            }}
                            className="text-xs text-zinc-400 hover:text-white px-3 py-1.5 rounded border border-zinc-700 hover:border-zinc-500 transition-colors"
                          >Update</button>
                          <button
                            disabled={isDeleting}
                            onClick={async () => {
                              setProviderKeyDeleting(p => ({ ...p, [provider]: true }));
                              try { await deleteProviderKey.mutateAsync(provider); } catch(e) {}
                              setProviderKeyDeleting(p => ({ ...p, [provider]: false }));
                            }}
                            className="p-2 text-zinc-600 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                          >
                            {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                          </button>
                        </div>
                      ) : isAdding ? (
                        <div className="flex items-center gap-2 flex-1">
                          <input
                            type="password"
                            placeholder={providerMeta.hint}
                            value={inputVal}
                            onChange={e => { setProviderKeyInputs(p => ({ ...p, [provider]: e.target.value })); setProviderKeyError(p => ({ ...p, [provider]: null })); }}
                            className="flex-1 bg-zinc-900 border border-zinc-700 focus:border-white/30 rounded px-3 py-1.5 text-sm font-mono text-white outline-none transition-colors"
                            autoFocus
                          />
                          <button
                            disabled={!inputVal || setProviderKey.isPending}
                            onClick={async () => {
                              try {
                                await setProviderKey.mutateAsync({ provider, apiKey: inputVal });
                                setProviderKeyInputs(p => ({ ...p, [provider]: '' }));
                                setProviderKeyAdding(p => ({ ...p, [provider]: false }));
                              } catch(e) {
                                setProviderKeyError(p => ({ ...p, [provider]: e?.response?.data?.detail || 'Failed to save' }));
                              }
                            }}
                            className="px-3 py-1.5 bg-white hover:bg-zinc-200 disabled:opacity-40 text-black text-sm rounded transition-colors"
                          >{setProviderKey.isPending ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Save'}</button>
                          <button onClick={() => { setProviderKeyAdding(p => ({ ...p, [provider]: false })); setProviderKeyInputs(p => ({ ...p, [provider]: '' })); }} className="p-2 text-zinc-600 hover:text-zinc-400 rounded transition-colors"><X className="w-4 h-4" /></button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setProviderKeyAdding(p => ({ ...p, [provider]: true }))}
                          className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-white transition-colors"
                        >
                          <Plus className="w-4 h-4" /> Add key
                        </button>
                      )}
                      {errMsg && <span className="text-xs text-red-400 ml-2">{errMsg}</span>}
                    </div>
                  );
                })}
              </div>
            )}
          </CardBody>
        </Card>


      </main>
    </SidebarLayout>
  );
}
